/**
 * Newsletter Queue Stack — Feb 2026
 * ==================================
 *
 * Shows the next N bi-weekly slots (default 4 = 8 weeks / ~2 months of
 * cadence coverage) so the founder can pre-load newsletters ahead of a
 * holiday, sabbatical, or travel window and the Tuesday cron consumes
 * them one at a time on schedule.
 *
 * Per slot:
 *   • Slot key + human-readable label (e.g. "2026-W28 · Tue 14 Jul")
 *   • If empty  → "Queue current draft" button (calls /drafts/promote-autosave)
 *   • If queued → title/message preview + "Load into editor" + "Remove"
 */
import React, { useEffect, useState, useCallback } from 'react';
import { CalendarClock, ArrowRightCircle, Loader2, Trash2, CheckCircle, Layers } from 'lucide-react';
import { apiClient } from '../../api/client';

const BRAND_PURPLE = '#7B4FD8';
const BRAND_ORANGE = '#F5831F';
const BRAND_INK = '#1A1209';

// ISO week (YYYY-Wnn) → Tuesday date of that week (in UTC).
const slotToTuesday = (slot) => {
  if (!slot) return null;
  const m = /^(\d{4})-W(\d{2})$/.exec(slot);
  if (!m) return null;
  const year = parseInt(m[1], 10);
  const week = parseInt(m[2], 10);
  // ISO week 1 = the week containing Jan 4th.
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const jan4Dow = jan4.getUTCDay() || 7; // 1..7 (Mon..Sun)
  const week1Monday = new Date(jan4);
  week1Monday.setUTCDate(jan4.getUTCDate() - jan4Dow + 1);
  const target = new Date(week1Monday);
  target.setUTCDate(week1Monday.getUTCDate() + (week - 1) * 7 + 1); // +1 = Tuesday
  return target;
};

const fmtSlot = (slot) => {
  const t = slotToTuesday(slot);
  if (!t) return slot;
  return t.toLocaleDateString(undefined, {
    weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC',
  }) + ' UTC';
};

const previewFor = (draft) => {
  if (!draft) return null;
  const bits = [];
  if (draft.main_message) bits.push(draft.main_message.slice(0, 80));
  if (draft.hero_cta_headline) bits.push(`CTA: "${draft.hero_cta_headline}"`);
  if (draft.posts?.length) bits.push(`${draft.posts.length} post${draft.posts.length === 1 ? '' : 's'}`);
  if (draft.hero_banner_url) bits.push('hero banner');
  return bits.join(' · ') || '(untitled)';
};

const NewsletterQueueStack = ({ onLoadIntoEditor }) => {
  const [stack, setStack] = useState([]);
  const [currentSlot, setCurrentSlot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busySlot, setBusySlot] = useState(null);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  // Feb 2026 — 4-slot (default, ~2 months) or 8-slot (one financial
  // quarter, useful for planning around a launch sprint or extended
  // leave). Persists across reload via localStorage.
  const [count, setCount] = useState(() => {
    const stored = parseInt(localStorage.getItem('hibff.newsletter.stack.count') || '4', 10);
    return stored === 8 ? 8 : 4;
  });

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const res = await apiClient.request(`/api/admin/newsletter/drafts/stack?count=${count}`);
      setStack(res.stack || []);
      setCurrentSlot(res.current_slot || null);
    } catch (e) {
      setErr(e.message || 'Failed to load stack');
    } finally {
      setLoading(false);
    }
  }, [count]);

  useEffect(() => { load(); }, [load]);

  const changeCount = (n) => {
    setCount(n);
    localStorage.setItem('hibff.newsletter.stack.count', String(n));
  };

  const promoteHere = async (slot) => {
    setBusySlot(slot); setMsg(null); setErr(null);
    try {
      const res = await apiClient.request(
        `/api/admin/newsletter/drafts/promote-autosave?slot=${encodeURIComponent(slot)}`,
        { method: 'POST' },
      );
      setMsg(`Queued to ${slot} · fields: ${(res.fields_persisted || []).join(', ') || 'none'}`);
      await load();
    } catch (e) {
      const detail = e?.message || 'Promote failed';
      setErr(detail.includes('No working draft') ? 'No working draft — compose something first, then Queue.' : detail);
    } finally {
      setBusySlot(null);
    }
  };

  const removeSlot = async (slot) => {
    if (!window.confirm(`Cancel the queued newsletter for ${slot}?`)) return;
    setBusySlot(slot);
    try {
      await apiClient.request(
        `/api/admin/newsletter/drafts?slot=${encodeURIComponent(slot)}`,
        { method: 'DELETE' },
      );
      setMsg(`Removed queued draft for ${slot}`);
      await load();
    } catch (e) { setErr(e.message || 'Remove failed'); }
    finally { setBusySlot(null); }
  };

  const loadIntoEditor = (draft, slot) => {
    if (typeof onLoadIntoEditor === 'function' && draft) onLoadIntoEditor(draft, slot);
  };

  return (
    <div
      className="rounded-2xl border-2 shadow-sm bg-white"
      style={{ borderColor: '#EDE7F8' }}
      data-testid="newsletter-queue-stack"
    >
      <div className="p-5 border-b flex items-center gap-3 flex-wrap" style={{ borderColor: '#F3F0FB' }}>
        <div
          className="rounded-xl p-2.5"
          style={{ backgroundColor: BRAND_PURPLE + '14', color: BRAND_PURPLE }}
        >
          <Layers size={22} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold" style={{ color: BRAND_INK }}>Queue Stack — pre-load newsletters ahead</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            Compose in the Editorial Studio, then Queue each slot. The Tuesday cron consumes them one-by-one so cadence keeps firing while you&apos;re away.
          </p>
        </div>
        {currentSlot && (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full"
            style={{ backgroundColor: '#F3F0FB', color: BRAND_PURPLE }}>
            <CalendarClock size={11} /> Current: {currentSlot}
          </span>
        )}
        {/* 4 vs 8 slot toggle */}
        <div
          className="inline-flex rounded-full overflow-hidden text-[11px] font-semibold"
          style={{ border: `1px solid ${BRAND_PURPLE}44` }}
          role="tablist"
          aria-label="Slots to display"
          data-testid="queue-stack-count-toggle"
        >
          {[4, 8].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => changeCount(n)}
              className="px-3 py-1 transition-colors"
              style={{
                backgroundColor: count === n ? BRAND_PURPLE : 'white',
                color: count === n ? 'white' : BRAND_PURPLE,
              }}
              aria-pressed={count === n}
              data-testid={`queue-stack-count-${n}`}
              title={n === 4 ? 'Next ~2 months of cadence' : 'Next ~1 quarter of cadence'}
            >
              {n} slots {n === 8 ? '(quarter)' : ''}
            </button>
          ))}
        </div>
      </div>

      {msg && (
        <div className="px-5 pt-3 text-sm flex items-center gap-2" style={{ color: '#065F46' }}>
          <CheckCircle size={14} /> {msg}
        </div>
      )}
      {err && (
        <div className="px-5 pt-3 text-sm" style={{ color: '#991B1B' }}>{err}</div>
      )}

      <div className="p-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 gap-3">
        {loading && !stack.length ? (
          <div className="col-span-full flex items-center justify-center py-6 text-gray-400">
            <Loader2 size={18} className="animate-spin mr-2" /> Loading stack…
          </div>
        ) : stack.map((row, idx) => {
          const draft = row.queued_draft;
          const isQueued = !!draft;
          const busy = busySlot === row.slot;
          return (
            <div
              key={row.slot}
              className="rounded-lg p-3 flex flex-col gap-2"
              style={{
                border: `1.5px ${isQueued ? 'solid' : 'dashed'} ${isQueued ? BRAND_PURPLE + '55' : '#E5E7EB'}`,
                backgroundColor: isQueued ? '#FAF8FE' : 'white',
              }}
              data-testid={`queue-stack-slot-${idx}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex flex-col">
                  <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: '#6B7280' }}>
                    Slot {idx + 1}
                  </span>
                  <span className="text-sm font-semibold" style={{ color: BRAND_INK }}>{row.slot}</span>
                  <span className="text-[11px] text-gray-500">{fmtSlot(row.slot)}</span>
                </div>
                {isQueued && (
                  <span
                    className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: BRAND_ORANGE + '22', color: BRAND_ORANGE }}
                  >
                    Queued
                  </span>
                )}
              </div>

              {isQueued ? (
                <>
                  <p className="text-xs leading-snug text-gray-700" data-testid={`queue-stack-preview-${idx}`}>
                    {previewFor(draft)}
                  </p>
                  <div className="flex items-center gap-2 mt-auto pt-2">
                    <button
                      type="button"
                      onClick={() => loadIntoEditor(draft, row.slot)}
                      className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 text-[11px] font-semibold rounded"
                      style={{ backgroundColor: 'white', color: BRAND_PURPLE, border: `1px solid ${BRAND_PURPLE}` }}
                      data-testid={`queue-stack-load-${idx}`}
                      title="Copy this queued draft back into the editor"
                    >
                      Load
                    </button>
                    <button
                      type="button"
                      onClick={() => removeSlot(row.slot)}
                      disabled={busy}
                      className="inline-flex items-center justify-center gap-1 px-2 py-1.5 text-[11px] font-semibold rounded disabled:opacity-50"
                      style={{ backgroundColor: '#FEE2E2', color: '#991B1B', border: '1px solid #FCA5A5' }}
                      data-testid={`queue-stack-remove-${idx}`}
                    >
                      {busy ? <Loader2 size={11} className="animate-spin" /> : <Trash2 size={11} />}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-xs text-gray-400 italic">Empty — nothing scheduled for this slot.</p>
                  <button
                    type="button"
                    onClick={() => promoteHere(row.slot)}
                    disabled={busy}
                    className="mt-auto inline-flex items-center justify-center gap-1 px-2 py-1.5 text-[11px] font-semibold rounded text-white disabled:opacity-50"
                    style={{ backgroundColor: BRAND_PURPLE }}
                    data-testid={`queue-stack-queue-${idx}`}
                    title="Copy the current working draft (from the Editorial Studio) into this slot"
                  >
                    {busy
                      ? <><Loader2 size={11} className="animate-spin" /> Queuing…</>
                      : <><ArrowRightCircle size={11} /> Queue current draft</>}
                  </button>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default NewsletterQueueStack;
